import { Button, Select } from "@/shared/ui/primitives";
import { Link } from "@tanstack/react-router";
import { ChevronDown, Headphones, Mic } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import type { CallLead } from "./call-recorder";
import { callRecordingSupport, meterSegments } from "./call-support";
import { LevelMeter } from "./level-meter";
import { saveMicrophone, savedMicrophone, useMicrophonePreview } from "./use-microphone-preview";

/** Chrome's entry for the microphone chosen in the system settings. */
const SYSTEM_MICROPHONE = "default";

/**
 * "Registra chiamata" in the bar on top (ADR 0067): the microphone with its
 * volume, then the start; the steps in Chrome open on request.
 */
export function CallSetupBar({
  lead,
  error,
  onStart,
  onClose,
}: {
  lead: CallLead;
  /** Why the last start failed, in Italian. */
  error: string | null;
  onStart: (microphoneId: string) => void;
  onClose: () => void;
}) {
  const microphoneId = useId();
  const support = useMemo(
    () =>
      callRecordingSupport({
        userAgent: navigator.userAgent,
        hasDisplayMedia: typeof navigator.mediaDevices?.getDisplayMedia === "function",
        hasMediaRecorder: typeof MediaRecorder !== "undefined",
      }),
    [],
  );
  // The system's microphone unless another one was chosen here: Chrome's own
  // preference can be a microphone that hears nothing (an iPhone nearby).
  const [microphone, setMicrophone] = useState(() => savedMicrophone() ?? SYSTEM_MICROPHONE);
  const [helpOpen, setHelpOpen] = useState(false);
  const preview = useMicrophonePreview(support.ok, microphone);
  const known = preview.devices.some((device) => device.id === microphone);
  // A microphone chosen last time and no longer connected: back to the system's.
  useEffect(() => {
    if (microphone === SYSTEM_MICROPHONE) return;
    if (preview.error || (preview.devices.length > 0 && !known)) setMicrophone(SYSTEM_MICROPHONE);
  }, [microphone, preview.error, preview.devices, known]);
  const hears = meterSegments(preview.level) >= 2;
  const message = !support.ok
    ? support.message
    : (error ??
      preview.error ??
      (hears
        ? "Ti sente: avvia quando la chiamata su WhatsApp Desktop è partita."
        : "Parla: le luci si devono accendere. Se restano spente, scegli un altro microfono."));
  const problem = !support.ok || Boolean(error || preview.error);

  return (
    <section className="call-bar-group" aria-label="Registra la chiamata">
      <div className="call-bar call-bar--setup">
        <span className="call-bar__text">
          <Mic aria-hidden="true" size={16} />
          Registra la chiamata con
          <Link
            to="/w/$workspaceSlug/inbox/$leadId"
            params={{ workspaceSlug: lead.workspaceSlug, leadId: lead.id }}
            className="call-bar__lead"
          >
            {lead.name}
          </Link>
        </span>
        {support.ok ? (
          <span className="call-bar__microphone">
            <label className="sr-only" htmlFor={microphoneId}>
              Il tuo microfono
            </label>
            <Select
              id={microphoneId}
              value={known ? microphone : SYSTEM_MICROPHONE}
              onChange={(event) => {
                setMicrophone(event.target.value);
                saveMicrophone(event.target.value);
              }}
            >
              {preview.devices.length === 0 ? (
                <option value={SYSTEM_MICROPHONE}>Microfono del sistema</option>
              ) : (
                preview.devices.map((device) => (
                  <option key={device.id} value={device.id}>
                    {device.label}
                  </option>
                ))
              )}
            </Select>
            <LevelMeter level={preview.level} label="Tu" />
          </span>
        ) : null}
        <span className="call-bar__actions">
          <button
            type="button"
            className="call-bar__toggle"
            aria-expanded={helpOpen}
            onClick={() => setHelpOpen((value) => !value)}
          >
            Come si fa <ChevronDown aria-hidden="true" size={14} />
          </button>
          <Button size="small" variant="quiet" onClick={onClose}>
            Chiudi
          </Button>
          <Button size="small" disabled={!support.ok} onClick={() => onStart(microphone)}>
            Avvia la registrazione
          </Button>
        </span>
        <p className={problem ? "call-bar__hint call-bar__hint--problem" : "call-bar__hint"}>
          {message}
        </p>
      </div>
      {helpOpen ? (
        <div className="call-bar__panel">
          <ol className="call-bar__steps">
            <li>Avvia la chiamata su WhatsApp Desktop, oppure rispondi.</li>
            <li>
              Clicca <strong>Avvia la registrazione</strong>.
            </li>
            <li>
              Nella finestra di condivisione scegli <strong>Schermo intero</strong>, attiva{" "}
              <strong>Condividi anche l'audio di sistema</strong> e conferma con{" "}
              <strong>Condividi</strong>.
            </li>
            <li>
              Durante la chiamata la trascrizione arriva a blocchi di mezzo minuto: la apri da{" "}
              <strong>Trascrizione</strong> in questa barra. A fine chiamata clicca{" "}
              <strong>Termina</strong>: Polaris prepara la proposta successiva.
            </li>
          </ol>
          <p className="call-bar__note">
            <Headphones aria-hidden="true" size={16} />
            Con le cuffie le due voci restano separate e la trascrizione è più precisa. La prima
            volta macOS può chiedere a Chrome il permesso "Registrazione schermo e audio di
            sistema".
          </p>
        </div>
      ) : null}
    </section>
  );
}
